import { $Q, $Qll } from '../utils/query-selector';
import { updateCart, updateCartLocalWithRemoteItems } from './cart';
import { syncCartRemote } from './cart-services-async';
import axios from 'axios';

/*
VAR GLOBAL WINDOW OBJECT add in snippet sidecart

CART_REMOTE
CUSTOMER_ID
*/

/**
Validates remote and local cart items and performs necessary actions.
@async
@function validateRemoteLocal
@returns {Promise<void>}
*/
export const validateRemoteLocal = async () => {
  if (typeof shopUrl === 'undefined') {
    console.error('Required globals are not defined.');
    return;
  }

  let {
    data: { items: cartCurrent },
  } = await axios.get(`${shopUrl}/cart.js`);

  const stringCompareLocal = cartCurrent
    .map((cart) => `${cart.variant_id}|${cart.quantity}`)
    .join(',');

  let validateStock = false;
  await getVariantsInventory(stringCompareLocal).then((inventoryData) => {
    if (inventoryData && !inventoryData.error) {
      cartCurrent = setVariantsInventory(cartCurrent, inventoryData);
      validateStock = true;
    }
  });

  stockValidation(cartCurrent, validateStock);

  if (CUSTOMER_ID) {
    const cartRemoteString = typeof CART_REMOTE !== 'undefined' ? CART_REMOTE.stringify : '';

    if (!compareStrings(cartRemoteString, stringCompareLocal)) {
      $Q('.overlay-async').classList.remove('hidden');
      await addNewItemsCart();
      $Q('.overlay-async').classList.add('hidden');
      $Q('.async-message').classList.remove('hidden');
      setTimeout(() => {
        $Q('.async-message').classList.add('hidden');
      }, 2000);
    }

    await updateCartRemote(cartCurrent);
  }

  showStockNotification(cartCurrent, validateStock);
};

/**
 * Stock validation of the cart items.
 * Update the cart items quantities to the available stock quantities.
 * If the stock is unavailable, the item quantity is set to 0.
 * @param {Array} cartCurrent - Array of current cart items.
 */
const stockValidation = (cartCurrent, validate) => {
  if (!validate) return;
  const itemsToUpdate = cartCurrent.filter((item) => item.inventoryQuantity === 0 || (item.inventoryQuantity < item.quantity));

  if (itemsToUpdate.length) {
    const cartItems = $Qll("#cart-items item-cart");
    let indexItem = 1;

    itemsToUpdate.forEach((item) => {
      cartItems.forEach((cartItem, index) => {
        const key = cartItem.getAttribute("data-live-item-key");
        if (key === item.key) {
          indexItem = index + 1;
        }
      })

      updateCart(indexItem, item.inventoryQuantity, `${item.id}-${indexItem}`);
    });
  }
};

/**
 * Shows a notification in the side cart when some items are out of stock
 * or their quantities are less than the available stock.
 * @param {Array} items - Array of items in the cart.
 */
const showStockNotification = (items, validate) => {
  if (!validate) return;
  const itemsToShow = items.filter((item) => item.inventoryQuantity === 0 || (item.inventoryQuantity < item.quantity));
  if (!itemsToShow.length) return;

  const modal = $Q('.side-cart__notification');
  const products = $Q('.side-cart__notification-products');

  products.innerHTML = itemsToShow.map((item) => (
    `<li>
      <img src="${item.image}" alt="${item.product_title}" />
      <p>${item.product_title}</p>
      <span>${item.inventoryQuantity === 0 ? cartStrings.outOfStock : cartStrings.changeQuantity}</span>
    </li>`
  )).join('');

  modal.classList.add('active');
};

/**
 * Updates the inventoryQuantity property for each item in itemsCurrent
 * with the value from the inventoryData.
 *
 * @param {Array} itemsCurrent - Array of items in the cart.
 * @param {Object} inventoryData - Object with data key containing an array of objects with id and inventory keys.
 * @returns {Array} Array of items with updated inventoryQuantity property.
 */
export const setVariantsInventory = (itemsCurrent, inventoryData) => {
  const items = itemsCurrent.items || itemsCurrent;

  inventoryData.data.forEach((inventoryItem) => {
    const item = items.find((item) => {
      const id = item.variantId || item.variant_id;
      return id === inventoryItem.id
    });
    if (item) {
      item.inventoryQuantity = inventoryItem.inventory;
    }
  })

  return itemsCurrent
}

/**
 * Fetches the inventory data for a given array of variant IDs.
 * @param {string} stringify - A string with variant IDs and their quantities
 * separated by commas and pipes (e.g. "1234567890|2,2345678901|3").
 * @returns {Promise<Object|Error>} A Promise that resolves to an object with
 * data key containing an array of objects with id and inventory keys, or
 * rejects with an Error if the request fails.
 */
export const getVariantsInventory = async (stringify) => {
  if (typeof routes === 'undefined') {
    console.error('Global variables are not defined.');
    return { error: true, message: 'Global variables undefined' };
  }

  const variantsIds = stringify.split(',').map((segment) => segment.split('|')[0]).join(',');
  const url = `${routes.back_url_cart}/customers/cart/inventory?variantIds=${encodeURIComponent(variantsIds)}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching inventory:", error);
    return { error: true, message: error.message };
  }
}

/**
Checks if remote cart items exist and returns the items array.
@function remoteExist
@returns {Array} - Array of remote cart items, or an empty array if not found.
*/
const remoteExist = () => {
  if (typeof CART_REMOTE !== 'undefined') {
    const cartRemote = CART_REMOTE;
    return cartRemote.items;
  }

  return [];
};

/**
Updates the remote cart with the current cart items.
note dev:
Send your local cart after updating, adding or removing
items from the cart to this function, to update the metafield.
@async
@function updateCartRemote
@param {Array} cartCurrent - Array of current cart items.
@returns {Promise<void>}
*/
const updateCartRemote = async (cartCurrent) => {
  if (!CUSTOMER_ID || typeof CUSTOMER_EMAIL === 'undefined') {
    console.error('Global variables are not defined.');
    return;
  }

  const userEmail = CUSTOMER_EMAIL;
  const userId = CUSTOMER_ID;

  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const cartRemoteItems = remoteExist();

  const itemsCurrent = cartCurrent.map((item) => ({
    productTitle: item.product_title,
    variantTitle: `${item.variant_title}`,
    variantId: item.variant_id,
    quantity: item.quantity,
  }));

  const itemsDuplicate = itemsCurrent.concat(cartRemoteItems);
  const itemsIUnique = getUniqueVariantItems(itemsDuplicate);

  const cartItemNew = {
    email: userEmail,
    updateAt: formattedDate,
    items: itemsIUnique,
  };

  await syncCartRemote(cartItemNew, userId);
};

/**
Retrieves an array of unique variant items from the given array of items.
@function getUniqueVariantItems
@param {Array} itemsDuplicate - Array of items with possible duplicates.
@returns {Array} - Array of unique variant items.
*/
const getUniqueVariantItems = (itemsDuplicate) => {
  const arrayItemsUnique = [];
  const uniqueVariantIds = new Set(itemsDuplicate.map((item) => item.variantId));
  uniqueVariantIds.forEach((id) => {
    const item = itemsDuplicate.find((item) => item.variantId === id);
    arrayItemsUnique.push(item);
  });

  return arrayItemsUnique;
};

/**
Adds new items from the remote cart to the local cart.
@async
@function addNewItemsCart
@returns {Promise<void>}
*/
const addNewItemsCart = async () => {
  const cartRemoteItems = remoteExist();

  if (!cartRemoteItems.length) return;

  const dataUpdate = {};

  for (const item of cartRemoteItems) {
    if (!(item.variantId in dataUpdate)) {
      dataUpdate[item.variantId] = item.quantity;
    } else {
      dataUpdate[item.variantId] = dataUpdate[item.variantId] + item.quantity;
    }
  }

  await updateCartLocalWithRemoteItems(dataUpdate);
};

/**

Compares two strings by creating maps of character counts for each string.
@function compareStrings
@param {string} str1 - First string to compare.
@param {string} str2 - Second string to compare.
@returns {boolean} - True if the strings have the same character counts, false otherwise.
*/
function compareStrings(str1, str2) {
  const map1 = createMap(str1);
  const map2 = createMap(str2);

  if (map1.size !== map2.size) {
    return false;
  }

  for (const [key, value] of map1) {
    if (!map2.has(key) || map2.get(key) !== value) {
      return false;
    }
  }

  return true;
}

function createMap(str) {
  const map = new Map();
  const pairs = str.split(',');

  for (const pair of pairs) {
    const [key, value] = pair.split('|');
    map.set(key, parseInt(value));
  }

  return map;
}
